import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import {
    MoreVerticalIcon,
    ExternalLinkIcon,
    PencilIcon,
    TrashIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ArrowUpDownIcon,
    MailIcon,
    SearchIcon,
    RefreshCcwDotIcon,
    WalletIcon,
    CheckCircle2Icon,
    BanIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DeactivateWorkspaceDialog } from "./dialog/dialog-deactivate-workspace";
import { WorkSpaceCompanyItem } from "@type/company";
import { EmptyState } from "@component/empty-state";
import { getListWorkspaceCompany, resendActivationLink, updateWorkspaceStatus } from "@service/employer";
import { formatHumanReadable, parseFormat } from "@util/date";
import { Input } from "@component/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { Pagination } from "@component/pagination";
import { MetaPagination } from "@type/common";
import { useSnackbar } from "@hook/use-snackbar";
import { SwitchWorkspaceDialog } from "./dialog/dialog-switch-workspace";
import { ActiveSubscriptionDialog, ActiveSubscriptionData } from "./dialog/dialog-active-subscription";
import { authSwitchWorkspace } from "@service/auth";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import { isOuraEnabled } from "@oura/role";

type WorkspaceTab = "activated" | "inactive";

// `lastLoginActivity` stays in the union: the column is gone from this table
// (#3416) but the BE still accepts the field, and dropping it here would be a
// silent contract change rather than a UI one.
type SortField = "companyName" | "dateCreated" | "linkExpiry" | "accessExpiry" | "lastLoginActivity" | "lastActivity" | null;
type SortDirection = "asc" | "desc" | null;

export function WorkspaceCompanyTable() {
    const snackbar = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [keywords, setKeywords] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState<WorkspaceTab>("activated");
    // Platform filter (company_type). "all" = no filter / every platform.
    const [companyTypeFilter, setCompanyTypeFilter] = useState<string>("all");
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })
    // #3416: the table opens on the most recently active workspaces. Date
    // created stops being useful past the first page; activity is the question
    // this screen is actually used to answer.
    const [sortField, setSortField] = useState<SortField>("lastActivity");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
    const [activeSubscriptionDialogOpen, setActiveSubscriptionDialogOpen] = useState(false);
    const [workspaces, setWorkspaces] = useState<WorkSpaceCompanyItem[]>([]);
    const [workspaceToDeactivate, setWorkspaceToDeactivate] = useState<{ id: string; name: string; type: "link" | "access" } | null>(null);
    const [workspaceToSwitch, setWorkspaceToSwitch] = useState<{ id: string; name: string } | null>(null);
    const [workspaceToActivate, setWorkspaceToActivate] = useState<{ id: string; name: string; email: string } | null>(null);

    const onLoad = (keywords: string, currentPage: number, sortField: SortField, sortDirection: SortDirection, status: WorkspaceTab = activeTab, companyType: string = companyTypeFilter) => {
        setIsLoading(true);
        getListWorkspaceCompany({
            keywords: keywords,
            workspace_status: status,
            company_type: companyType === "all" ? undefined : companyType,
            sort_field: sortField || undefined,
            sort_direction: sortDirection || undefined,
            page: currentPage,
            limit: 10,
        }).then((resp) => {
            if (resp.status === 200) {
                setWorkspaces(resp.data.data);
                setMetaPagination(resp.data.meta.pagination);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }

    // Switching tabs refetches with the new workspace_status and resets to page 1.
    const handleTabChange = (tab: WorkspaceTab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        setCurrentPage(1);
        onLoad(keywords, 1, sortField, sortDirection, tab);
    };

    // Selecting a platform refetches with company_type and resets to page 1.
    const handleCompanyTypeFilter = (value: string) => {
        if (value === companyTypeFilter) return;
        setCompanyTypeFilter(value);
        setCurrentPage(1);
        onLoad(keywords, 1, sortField, sortDirection, activeTab, value);
    };

    useEffect(() => {
        onLoad(keywords, currentPage, sortField, sortDirection);
    }, [])

    const onLoadPagination = (page: number) => {
        setCurrentPage(page);
        onLoad(keywords, page, sortField, sortDirection);
    }

    const onChangeSearchBar = (e: React.ChangeEvent<HTMLInputElement>) => {
        setKeywords(e.target.value);
        if (e.target.value === "") {
            onLoad("", currentPage, sortField, sortDirection);
        }
    }

    const handleSort = (field: SortField) => {
        let newDirection: SortDirection;

        if (sortField === field) {
            newDirection = sortDirection === "asc" ? "desc" : "asc";
        } else {
            newDirection = "asc";
        }

        setSortField(field);
        setSortDirection(newDirection);
        onLoad(keywords, currentPage, field, newDirection);
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <ArrowUpDownIcon className="h-4 w-4 text-gray-400" />;
        }
        if (sortDirection === "asc") {
            return <ArrowUpIcon className="h-4 w-4 text-gray-600" />;
        }
        return <ArrowDownIcon className="h-4 w-4 text-gray-600" />;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Activated":
                return "bg-green-100 text-green-800 hover:bg-green-100";
            case "Pending Activation":
                return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
            case "Link Expired":
                return "bg-red-100 text-red-800 hover:bg-red-100";
            case "Access Expired":
                return "bg-red-100 text-red-800 hover:bg-red-100";
            case "Link Deactivated":
                return "bg-gray-100 text-gray-800 hover:bg-gray-100";
            case "Access Deactivated":
                return "bg-gray-100 text-gray-800 hover:bg-gray-100";
            default:
                return "";
        }
    };

    // Human label for the company_type product kind (hr_screening | agent_calling | training-agent).
    const companyTypeLabel = (type?: string) => {
        switch (type) {
            case "hr_screening":
                return "HR Screening";
            case "agent_calling":
                return "Agent Calling";
            case "training-agent":
                return "Training";
            case "oura":
                return "Oura";
            default:
                return type || "";
        }
    };

    const handleDeactivateLinkClick = (id: string, name: string) => {
        setWorkspaceToDeactivate({ id, name, type: "link" });
        setDeactivateDialogOpen(true);
    };

    const handleResendActivationLink = (id: string) => {
        resendActivationLink(id).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Activation link resent successfully.");
            }
        }).catch(() => {
            snackbar.showError("Failed to resend activation link.");
            onLoad(
                keywords,
                currentPage,
                sortField,
                sortDirection
            );
        })
    };

    const handleDeactivateAccessClick = (id: string, name: string) => {
        setWorkspaceToDeactivate({ id, name, type: "access" });
        setDeactivateDialogOpen(true);
    };

    const handleReactivateLink = (id: string) => {
        updateWorkspaceStatus(id, "reactivate_link").then((resp) => {
            if (resp.status === 200) {
                onLoad(
                    keywords,
                    currentPage,
                    sortField,
                    sortDirection
                )
            }
        })
    };

    const handleReactivateAccess = (id: string) => {
        updateWorkspaceStatus(id, "reactivate_access").then((resp) => {
            if (resp.status === 200) {
                onLoad(
                    keywords,
                    currentPage,
                    sortField,
                    sortDirection
                );
            }
        })
    };

    const handleDeactivateConfirm = (type: "link" | "access") => {
        updateWorkspaceStatus(
            workspaceToDeactivate?.id as string,
            type === "link" ? "deactivate_link" : "deactivate_access",
        ).then((resp) => {
            if (resp.status === 200) {
                setWorkspaceToDeactivate(null);
                onLoad(
                    keywords,
                    currentPage,
                    sortField,
                    sortDirection
                );
            }
        })
    };

    const handleActivateSubscription = (entry: WorkSpaceCompanyItem) => {
        setWorkspaceToActivate({ id: entry.company_id, name: entry.company_name, email: entry.email });
        setActiveSubscriptionDialogOpen(true);
    };

    const handleActivateSubscriptionConfirm = (_data: ActiveSubscriptionData) => {
        // The activation itself runs inside ActiveSubscriptionDialog
        // (setupSubscription); here we just close the dialog and reset.
        setActiveSubscriptionDialogOpen(false);
        setWorkspaceToActivate(null);
    };

    const handleSwitchWorkspace = (id: string, name: string) => {
        setSwitchDialogOpen(true);
        setWorkspaceToSwitch({ id, name });
    }

    const handleSwitchConfirm = () => {
        if (!workspaceToSwitch) return;

        authSwitchWorkspace(workspaceToSwitch?.id as string).then((resp) => {
            if (resp.status === 200) {
                const token = encodeURIComponent(resp.data.data.access_token);
                window.open(`https://${resp.data.data.token_domain}/switch-workspace?token=${token}`, "_blank");
            }
        }).catch((error) => {
            snackbar.showError("Failed to switch workspace.");
            catchErrorLog(`failed to switch workspace: ${error}`);
        }).finally(() => {
            setSwitchDialogOpen(false);
            setWorkspaceToSwitch(null);
        });
    }

    return (
        <>
            {/* Search + platform (company_type) filter */}
            <form onSubmit={(e) => {
                e.preventDefault();
                onLoad(keywords, currentPage, sortField, sortDirection);
            }}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative flex-1 min-w-0">
                        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                            type="text"
                            placeholder="Search companies..."
                            value={keywords}
                            onChange={onChangeSearchBar}
                            className="pl-10 h-12 bg-white border-gray-300"
                        />
                    </div>
                    <Select value={companyTypeFilter} onValueChange={handleCompanyTypeFilter}>
                        <SelectTrigger className="h-12 w-full flex-shrink-0 bg-white border-gray-300 sm:w-[200px]">
                            <SelectValue placeholder="All Platforms" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Platforms</SelectItem>
                            <SelectItem value="hr_screening">HR Screening</SelectItem>
                            <SelectItem value="agent_calling">Agent Calling</SelectItem>
                            <SelectItem value="training-agent">Training</SelectItem>
                            {/* Hidden while Oura is off in production — filtering by a
                                product that cannot be created yet only invites the
                                question. The row LABEL below stays either way, so any
                                pre-existing Oura workspace still renders by name. */}
                            {isOuraEnabled() && <SelectItem value="oura">Oura</SelectItem>}
                        </SelectContent>
                    </Select>
                </div>
                <button type="submit" className="hidden">submit</button>
            </form>

            {/* Status tabs — each refetches with workspace_status (activated / inactive). */}
            <div className="flex border-b border-gray-200 mb-4">
                {([
                    { key: "activated" as const, label: "Activated", icon: CheckCircle2Icon },
                    { key: "inactive" as const, label: "Inactivated", icon: BanIcon },
                ]).map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => handleTabChange(tab.key)}
                            className={cn(
                                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
                                isActive
                                    ? "border-blue-600 text-blue-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700",
                            )}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            <div className="overflow-x-auto">
            <table className="w-full min-w-max">
                <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">
                            <button
                                onClick={() => handleSort("companyName")}
                                className="flex items-center gap-1 uppercase tracking-wider transition-colors select-none hover:text-gray-800"
                            >
                                Company
                                {getSortIcon("companyName")}
                            </button>
                        </th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Workspace Type</th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                            <button
                                onClick={() => handleSort("dateCreated")}
                                className="flex w-full items-center justify-center gap-1 uppercase tracking-wider transition-colors select-none hover:text-gray-800"
                            >
                                Date Created
                                {getSortIcon("dateCreated")}
                            </button>
                        </th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                            <button
                                onClick={() => handleSort("linkExpiry")}
                                className="flex w-full items-center justify-center gap-1 uppercase tracking-wider transition-colors select-none hover:text-gray-800"
                            >
                                Link Expiry
                                {getSortIcon("linkExpiry")}
                            </button>
                        </th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                            <button
                                onClick={() => handleSort("accessExpiry")}
                                className="flex w-full items-center justify-center gap-1 uppercase tracking-wider transition-colors select-none hover:text-gray-800"
                            >
                                Access Expiry
                                {getSortIcon("accessExpiry")}
                            </button>
                        </th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Workspace Status</th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">
                            <button
                                onClick={() => handleSort("lastActivity")}
                                className="flex w-full items-center justify-center gap-1 uppercase tracking-wider transition-colors select-none hover:text-gray-800"
                            >
                                Last Activity
                                {getSortIcon("lastActivity")}
                            </button>
                        </th>
                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {workspaces.length === 0 && (
                        <tr className="hover:bg-gray-50">
                            <td colSpan={8} className="pl-6 pr-4">
                                <EmptyState
                                    title={activeTab === "activated" ? "No activated workspaces." : "No inactive workspaces."}
                                    description={
                                        activeTab === "activated"
                                            ? "Activated company workspaces will appear here."
                                            : "Workspaces with an expired link or deactivated access will appear here."
                                    }
                                />
                            </td>
                        </tr>
                    )}
                    {workspaces.map((entry, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4 whitespace-nowrap text-left">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center bg-blue-50">
                                        <img
                                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${entry.company_logo}`}
                                            alt={`${entry.company_name} logo`}
                                            onError={(e) => {
                                                e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                                            }}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-sm font-medium text-gray-900 truncate">{entry.company_name}</span>
                                            {entry.company_type && (
                                                <span className="inline-flex flex-shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                                    {companyTypeLabel(entry.company_type)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate mt-0.5">{entry.email}</div>
                                        {entry.workspace_url && (
                                            <a
                                                href={entry.workspace_url}
                                                title={entry.workspace_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="mt-0.5 inline-flex max-w-full items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                            >
                                                <span className="truncate">{entry.workspace_url}</span>
                                                <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-sm text-gray-700">
                                    {entry.current_subscription_package || entry.workspace_subscription_active || entry.workspace_type}
                                    {entry.company_currency && (
                                        <span className="text-gray-400"> · {entry.company_currency}</span>
                                    )}
                                </div>
                                {entry.subscription_end_date && (
                                    <div className="text-xs text-gray-400 mt-0.5">
                                        Ends {parseFormat(entry.subscription_end_date, "D MMM YYYY")}
                                    </div>
                                )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                                <div className="text-sm text-gray-500">{parseFormat(entry.date_created, "D MMM YYYY")}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                                <div className="text-sm text-gray-500">{entry.link_expiry ? parseFormat(entry.link_expiry, "D MMM YYYY") : <span className="text-gray-400">—</span>}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                                <div className="text-sm text-gray-500">{entry.access_expiry ? parseFormat(entry.access_expiry, "D MMM YYYY") : <span className="text-gray-400">—</span>}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                                <Badge
                                    variant="secondary"
                                    className={getStatusColor(entry.workspace_status)}
                                    style={{
                                        paddingTop: "4px",
                                        paddingBottom: "4px",
                                        borderRadius: "1rem",
                                    }}
                                >
                                    {entry.workspace_status}
                                </Badge>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                                {/* Newest job create/edit/status-change or member
                                    login (#3416). `—` when the BE reports null:
                                    never active, which is not the same as a very
                                    old date and must not read as one. */}
                                <div className="text-sm text-gray-500">{entry.last_activity_at ? formatHumanReadable(entry.last_activity_at, true) : <span className="text-gray-400">—</span>}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                        >
                                            <MoreVerticalIcon className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        {(entry.workspace_status === "Link Expired" || entry.workspace_status === "Link Deactivated") && (
                                            <>
                                                <DropdownMenuItem onClick={() => handleReactivateLink(entry.company_id)}>
                                                    <MailIcon className="h-4 w-4 mr-2" />
                                                    Reactivate Link
                                                </DropdownMenuItem>
                                                <Link to={`/workspaces/${entry.company_id}/edit`}>
                                                    <DropdownMenuItem>
                                                        <PencilIcon className="h-4 w-4 mr-2" />
                                                        Edit
                                                    </DropdownMenuItem>
                                                </Link>
                                            </>
                                        )}
                                        {(entry.workspace_status === "Access Expired" || entry.workspace_status === "Access Deactivated") && (
                                            <>
                                                {/* An expired trial's natural next step is paying — offer the
                                                    same subscription dialog the Activated branch gets. */}
                                                {entry.workspace_status === "Access Expired" && (
                                                    <DropdownMenuItem onClick={() => handleActivateSubscription(entry)}>
                                                        <WalletIcon className="h-4 w-4 mr-2" />
                                                        Activate Subscription
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuItem onClick={() => handleReactivateAccess(entry.company_id)}>
                                                    <MailIcon className="h-4 w-4 mr-2" />
                                                    Reactivate Access
                                                </DropdownMenuItem>
                                                <Link to={`/workspaces/${entry.company_id}/edit`}>
                                                    <DropdownMenuItem>
                                                        <PencilIcon className="h-4 w-4 mr-2" />
                                                        Edit
                                                    </DropdownMenuItem>
                                                </Link>
                                            </>
                                        )}
                                        {entry.workspace_status === "Pending Activation" && (
                                            <>
                                                <DropdownMenuItem onClick={() => handleResendActivationLink(entry.company_id)}>
                                                    <MailIcon className="h-4 w-4 mr-2" />
                                                    Resend Activation Link
                                                </DropdownMenuItem>
                                                <DropdownMenuItem className="text-red-600" onClick={() => handleDeactivateLinkClick(entry.company_id, entry.company_name)}>
                                                    <TrashIcon className="h-4 w-4 mr-2" />
                                                    Deactivate Link
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        {entry.workspace_status === "Activated" && (
                                            <>
                                                <DropdownMenuItem onClick={() => handleActivateSubscription(entry)}>
                                                    <WalletIcon className="h-4 w-4 mr-2" />
                                                    Activate Subscription
                                                </DropdownMenuItem>
                                                <DropdownMenuItem className="text-red-600" onClick={() => handleDeactivateAccessClick(entry.company_id, entry.company_name)}>
                                                    <TrashIcon className="h-4 w-4 mr-2" />
                                                    Deactivate Access
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        <DropdownMenuItem onClick={() => handleSwitchWorkspace(entry.company_id, entry.company_name)}>
                                            <RefreshCcwDotIcon className="h-4 w-4 mr-2" />
                                            Switch Access
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            <div className="p-4">
                <Pagination
                    isLoading={isLoading}
                    currentPage={currentPage}
                    totalPages={metaPagination.total_pages || 0}
                    onPageChange={onLoadPagination}
                />
            </div>
            <DeactivateWorkspaceDialog
                isOpen={deactivateDialogOpen}
                onClose={() => {
                    setDeactivateDialogOpen(false);
                    setWorkspaceToDeactivate(null);
                }}
                onConfirm={handleDeactivateConfirm}
                type={workspaceToDeactivate?.type}
            />

            <SwitchWorkspaceDialog
                isOpen={switchDialogOpen}
                onClose={() => {
                    setSwitchDialogOpen(false);
                }}
                companyName={workspaceToSwitch?.name || ""}
                onConfirm={handleSwitchConfirm}
            />

            <ActiveSubscriptionDialog
                isOpen={activeSubscriptionDialogOpen}
                onClose={() => {
                    setActiveSubscriptionDialogOpen(false);
                    setWorkspaceToActivate(null);
                }}
                onConfirm={handleActivateSubscriptionConfirm}
                companyId={workspaceToActivate?.id || ""}
                companyName={workspaceToActivate?.name || ""}
                ownerName={workspaceToActivate?.name || ""}
                ownerEmail={workspaceToActivate?.email || ""}
            />
        </>
    );
}
